import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useRecentSplit } from '@tunasong/search'
import { EntityGrid, Typography, useFavorites, useNavigateToEntity } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useRef } from 'react'
import { useIsSoloUser } from './use-is-solo-user.js'
import { useSectionPinShortcut } from './use-section-pin-shortcut.js'
import styles from './recently-updated-section.module.css'

/**
 * Dashboard Phase 2 — collaborator-driven updates only.
 *
 * Source: `useRecentSplit().theirs` (server-side recent feed, client-side filter
 * `lastEditedBy !== me.userId`). Pinned entities are filtered out so an item appears
 * in exactly one section.
 */
export const RecentlyUpdatedSection: FC = () => {
  const { theirs, isLoading: isLoadingSplit } = useRecentSplit()
  const { isPinned } = useFavorites()
  const navigateToEntity = useNavigateToEntity()
  const isSolo = useIsSoloUser()
  const sectionRef = useRef<HTMLElement>(null)

  const theirIds = theirs.map(e => e.id)
  const { existingEntities = [], isLoading: isResolving } = graphHooks.useEntitiesById(theirIds)
  const entitiesById = new Map(existingEntities.map(e => [e.id, e]))

  const items: Persisted<Entity>[] = theirIds
    .map(id => entitiesById.get(id))
    .filter((e): e is Persisted<Entity> => Boolean(e) && !e?.trash)
    .filter(e => !isPinned(e.id))

  const loading = isLoadingSplit || isResolving

  useSectionPinShortcut({ sectionRef, entities: items })

  if (!loading && items.length === 0) {
    return null
  }

  return (
    <section ref={sectionRef} className={styles.section} aria-labelledby="dash-recently-updated">
      <Typography id="dash-recently-updated" className={styles.heading} variant="h2">
        Recently updated
      </Typography>
      <Typography className={styles.caption} variant="caption">
        Latest changes across your spaces — yours and your collaborators'.
      </Typography>
      <EntityGrid entities={items} onOpen={navigateToEntity} size="medium" showLastEditor={!isSolo} showParentSpace />
    </section>
  )
}

export default RecentlyUpdatedSection
